import { makeRequest } from '@/network/api';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useSignMessage } from 'wagmi';

import { toaster } from '@/components/ui/toaster';
import { AuthContext } from '@/hooks/useAuth';
import { useAuthenticatedLoadingState } from '@/hooks/useAuthenticatedLoadingState';
import { useCompliance } from '@/hooks/useCompliance';
import { trackUserLogin } from '@/utils';
import { usePrevious, useUpdateEffect } from '@chakra-ui/react';
import { useAppKitAccount, useDisconnect } from '@reown/appkit/react';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isConnected, address, status } = useAppKitAccount();
  const { disconnect } = useDisconnect();
  const [authedViaLogin, setAuthedViaLogin] = useState(false);
  const { signMessageAsync } = useSignMessage();
  const { setAuthenticatedLoadingState } = useAuthenticatedLoadingState();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState<string | undefined>(undefined);
  const setAuthenticationState = useCallback(
    (authState: boolean) => {
      setIsAuthenticated(authState);
      setAuthenticatedLoadingState(authState);
    },
    [setAuthenticatedLoadingState, setIsAuthenticated],
  );
  const { geoCompliance } = useCompliance();
  const [isLoading, setIsLoading] = useState(true);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const [searchParams] = useSearchParams();

  // ✅ Capture query param on first render
  const [referrerFromUrl, setReferrerFromUrl] = useState(() => searchParams.get('referrer'));

  useEffect(() => {
    if (!referrerFromUrl) {
      setReferrerFromUrl(searchParams.get('referrer')); // Set only if not already stored
    }
  }, [searchParams, referrerFromUrl]);

  const setAuthStateFromLoginResponse = useCallback(
    async (resJson: { error: string; hasCompletedOnboarding: boolean; role?: string }) => {
      try {
        if (resJson.error) {
          setAuthenticationState(false);
          setHasCompletedOnboarding(false);
          setRole(undefined);
          throw new Error(resJson?.error ?? 'Error with login response');
        }
        setAuthenticationState(true);
        setHasCompletedOnboarding(resJson.hasCompletedOnboarding);
        setRole(resJson.role);
        if (!resJson.hasCompletedOnboarding && !currentPath.startsWith('/onboarding')) {
          navigate('/onboarding/welcome');
        }

        return !resJson.error;
      } catch (err) {
        console.error('Error setting auth state from login response', err);
        return false;
      }
    },
    [currentPath, navigate, setAuthenticationState],
  );

  const login = useCallback(
    async (address: string, referredByCode: string | null) => {
      try {
        setIsLoading(true);
        if (!address) return;

        const res = await makeRequest(`auth/sigMessage?walletAddress=${address}`);
        const { message } = await res.json();
        const signature = await signMessageAsync({ message });
        const authRes = await makeRequest('auth/login', {
          method: 'POST',
          body: JSON.stringify({ walletAddress: address, signature, referredByCode }),
        });
        const authResJson = await authRes.json();
        const success = await setAuthStateFromLoginResponse(authResJson);
        if (success) {
          // GA login tracking
          setAuthedViaLogin(true);
          trackUserLogin(address);
        }
        if (authResJson.userAlreadyExistsButTriedToRedeemReferral) {
          toaster.create({
            type: 'error',
            title: 'Could Not Redeem Referral Code',
            description:
              'This account already exists. The referring user will not get credit for actions taken on this wallet.',
            duration: 30_000,
          });
        }
        return success;
      } catch (error) {
        console.error('Authentication failed:', error);
        setAuthenticationState(false);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setAuthStateFromLoginResponse, setAuthenticationState, signMessageAsync],
  );

  const reconnect = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await makeRequest('auth/checkSession');
      const success = await setAuthStateFromLoginResponse(await res.json());
      if (success && address) {
        // GA login tracking
        trackUserLogin(address);
      }
      return success;
    } catch (error) {
      console.error('Reauth failed:', error);
      // Explicitly clear auth state on session check failure
      setAuthenticationState(false);
      setHasCompletedOnboarding(false);
      setRole(undefined);
      setIsLoading(false);
      return false; // Explicitly return false instead of undefined
    } finally {
      setIsLoading(false);
    }
  }, [address]);

  const logout = useCallback(async () => {
    try {
      await makeRequest('auth/logout', { method: 'POST' });
      await disconnect();
      setAuthedViaLogin(false);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setAuthenticationState(false);
      setRole(undefined);
      setHasCompletedOnboarding(false);
      setIsLoading(false);
    }
  }, [disconnect]);

  useEffect(() => {
    const attemptReauth = async () => {
      if (geoCompliance?.restrictAccess) return;
      const hasLiveSession = await reconnect();
      if (!hasLiveSession && address) {
        const success = await login(address, referrerFromUrl);
        if (!success) {
          await logout();
        }
      }
    };
    if (status === 'connecting') return;
    if (!isAuthenticated) {
      attemptReauth();
    }
    // `login`, `logout`, `reconnect` are intentionally excluded to avoid infinite loops.
    // They are stable via useCallback and safe to use here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, isConnected, status, isAuthenticated, referrerFromUrl, geoCompliance?.restrictAccess]);
  const previousAddress = usePrevious(address);

  useUpdateEffect(() => {
    const relog = async () => {
      // Remove debug log
      if (address) {
        try {
          await makeRequest('auth/logout', { method: 'POST' });
        } catch (error) {
          console.error('Error logging out', error);
        } finally {
          setAuthenticationState(false);
          setIsLoading(false);
        }
      }
    };

    if (address && previousAddress && previousAddress !== address) {
      relog();
    }
  }, [address, previousAddress]);

  useUpdateEffect(() => {
    if (geoCompliance?.restrictAccess) {
      logout();
    }
  }, [geoCompliance?.restrictAccess, isAuthenticated]);
  return (
    <AuthContext.Provider
      value={{
        address: address as EthAddress,
        isAuthenticated,
        isLoading,
        authedViaLogin,
        logout: logout,
        hasCompletedOnboarding,
        reconnect,
        referrerFromUrl,
        role,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
